import { API_ERROR, FEEDBACK_CLICK, ON_FEDERATED_LOGIN_MODAL_PAGE_VIEW, SET_HF_FOOT_SUBSCRIBE_SUBMIT } from 'constants/reduxActions';
import {
  CREATE_ACCOUNT_BTN,
  federatedLoginModalEventMap,
  isFederatedLoginModalSignInEvent,
  SIGN_IN_WITH_AMAZON_BTN,
  SIGN_IN_WITH_APPLE_BTN,
  SIGN_IN_WITH_GOOGLE_BTN,
  SIGN_IN_WITH_ZAPPOS_BTN
} from 'constants/federatedLoginModalInteractionTypes';
import { middlewareTrack } from 'apis/amethyst';
import { getAmethystPageType, trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { STORE_REWARDS_TRANSPARENCY_POINTS_FOR_ITEM } from 'store/ducks/rewards/types';
import { cartModalClose, cartView, modifyQuantity, navigateToCheckout, removeFromCart, viewCartClick } from 'events/cart';
import {
  CART_PAGEVIEW,
  EVENT_CART_MODAL_CLOSE,
  EVENT_CART_RECO_CLICK,
  EVENT_MODIFY_QUANTITY,
  EVENT_NAVIGATE_TO_CHECKOUT,
  EVENT_REMOVE_FROM_CART,
  EVENT_VIEW_CART_CLICK
} from 'store/ducks/cart/types';
/*
  Global Events:
  This will allow you to fire events based on client-side actions across the site
*/

const EVENT_CART_MODAL_VIEW = 'EVENT_CART_MODAL_VIEW';

export const cartModalViewEvent = () => ({
  type: EVENT_CART_MODAL_VIEW
});

/**
 * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SentimentFeedback.proto
 *
 * @param {boolean} feedback - Customer input on whether search results were helpful -> this comes from the 'feedback' payload from the Redux action
 */
const evFeedbackClick = (_, { feedback, pageType, feedbackType }) => {
  middlewareTrack({
    sentimentFeedback: {
      pageType,
      feedbackType,
      positive: feedback,
      negative: !feedback
    }
  });
};

const evOnApiError = (_, { pageType, apiErrorType, endpointUrl }) => {
  middlewareTrack({
    apiError: {
      pageType,
      apiErrorType,
      endpointUrl
    }
  });
};

const evOnShowRewardsTransparencyPdp = appState => {
  const {
    customer: isRecognizedCustomer,
    pageView: { pageType },
    sharedRewards: { transparencyPointsForItem, isEnrolled }
  } = appState;

  if (transparencyPointsForItem?.totalPoints?.points) {
    const {
      totalPoints: { points, dollarValue }
    } = transparencyPointsForItem;

    // ZFC event
    const recognizedStatus = isRecognizedCustomer ? 'recognized' : 'unrecognized';
    const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
    trackEvent('TE_SHOW_REWARDS_TRANSPARENCY', `${pageType}:${points}:${recognizedStatus}:${enrolledStatus}`);

    // Amethyst event
    middlewareTrack({
      rewardsTransparencyImpression: {
        sourcePage: getAmethystPageType(pageType),
        isEnrolled: !!isEnrolled,
        rewardsPoints: points,
        rewardsDollars: dollarValue
      }
    });
  }
};

const evOnShowRewardsTransparencyCartModal = appState => {
  const {
    customer: isRecognizedCustomer,
    sharedRewards: { transparencyPointsForCart, isEnrolled }
  } = appState;

  if (transparencyPointsForCart?.totalPoints?.points) {
    const {
      totalPoints: { points, dollarValue }
    } = transparencyPointsForCart;

    // ZFC event
    const recognizedStatus = isRecognizedCustomer ? 'recognized' : 'unrecognized';
    const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
    trackEvent('TE_SHOW_REWARDS_TRANSPARENCY', `cartModal:${points}:${recognizedStatus}:${enrolledStatus}}`);

    // Amethyst event
    middlewareTrack({
      rewardsTransparencyImpression: {
        sourcePage: 'CART_PAGE_MODAL',
        isEnrolled: !!isEnrolled,
        rewardsPoints: points,
        rewardsDollars: dollarValue
      }
    });
  }
};

export const evOnShowRewardsTransparencyClick = ({ isForCartModal, pageType, rewards, rewardsForCurrentPage, isRecognizedCustomer }) => {
  const { isEnrolled, transparencyPointsForCart } = rewards;
  const recognizedStatus = isRecognizedCustomer ? 'recognized' : 'unrecognized';
  const currentRewards = isForCartModal ? transparencyPointsForCart : rewardsForCurrentPage;
  const { totalPoints: { points, dollarValue } = {} } = currentRewards;
  const enrolledStatus = isEnrolled ? 'enrolled' : 'unenrolled';
  const view = isForCartModal ? 'cartModal' : pageType;

  trackEvent('TE_CLICK_REWARDS_TRANSPARENCY', `${view}:${points}:${recognizedStatus}:${enrolledStatus}`);

  return {
    rewardsTransparencyClick: {
      sourcePage: getAmethystPageType(pageType),
      isEnrolled: !!isEnrolled,
      rewardsPoints: points,
      rewardsDollars: dollarValue
    }
  };
};

// Login method mapping for LoginStarted event
// Values correspond to LoginMethodType enum in WebsiteEnums.proto
const LOGIN_METHOD_MAP = {
  [SIGN_IN_WITH_ZAPPOS_BTN]: 'LOGIN_WITH_ZAPPOS',
  [SIGN_IN_WITH_AMAZON_BTN]: 'LOGIN_WITH_AMAZON',
  [SIGN_IN_WITH_GOOGLE_BTN]: 'LOGIN_WITH_GOOGLE',
  [SIGN_IN_WITH_APPLE_BTN]: 'LOGIN_WITH_APPLE'
};

// amethyst event: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/FederatedLoginModalInteraction.proto
export const evFederatedLoginModalInteraction = (interactionType, sourcePage) => {
  // zfc event
  const interactionEvent = federatedLoginModalEventMap[interactionType];
  if (interactionEvent) {
    trackEvent(interactionEvent);
  }

  if (isFederatedLoginModalSignInEvent(interactionEvent)) {
    trackEvent('TE_HEADER_ACCOUNT_SIGNIN');
    trackLegacyEvent('Main-Nav', 'SignIn', 'LoginRegister');
  }

  // LoginStarted event — fires for Zappos, Amazon, and Google sign-in clicks
  const loginMethod = LOGIN_METHOD_MAP[interactionType];
  if (loginMethod && sourcePage) {
    middlewareTrack({
      loginStarted: {
        loginMethod,
        sourcePage
      }
    });
  }

  // RegisterStarted event — fires only for Create Account clicks
  if (Number(interactionType) === CREATE_ACCOUNT_BTN && sourcePage) {
    middlewareTrack({
      registerStarted: {
        sourcePage
      }
    });
  }

  middlewareTrack({
    federatedLoginModalInteraction: {
      interactionType
    }
  });
};

// amethyst event: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/FederatedLoginModalView.proto
export const evOnShowFederatedLoginModal = ({ pageView: { pageType: pageTypeFromState } }, { sourcePageType }) => {
  const sourcePage = getAmethystPageType(sourcePageType || pageTypeFromState);

  // ZFC event
  trackEvent('TE_SHOW_FEDERATED_LOGIN_MODAL');

  // Amethyst event
  middlewareTrack({
    federatedLoginModalView: {
      sourcePage
    }
  });
};

// amethyst event: https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/BroadcastEmailSignup.proto
export const handleSubscribeSubmitted = ({ pageView: { pageType: pageTypeFromState } }, { sourcePageType }) => {
  const sourcePage = getAmethystPageType(sourcePageType || pageTypeFromState);

  // ZFC event — fires unconditionally for any email subscribe surface (footer, drawer, etc.)
  trackEvent('TE_FOOTER_SUBSCRIBE');

  // Amethyst event
  middlewareTrack({
    broadcastEmailSignup: {
      sourcePage
    }
  });
};

export const cartRecommendationClick = (appState, { productId, index, widgetType }) => {
  const {
    pageView: { pageType }
  } = appState;
  middlewareTrack({
    recommendationClick: {
      index,
      widgetType,
      recommendationType: 'PRODUCT_RECOMMENDATION',
      recommendationValue: productId,
      recommendationSource: 'EP13N',
      sourcePage: getAmethystPageType(pageType)
    }
  });
};

export default {
  events: {
    [FEEDBACK_CLICK]: [evFeedbackClick],
    [STORE_REWARDS_TRANSPARENCY_POINTS_FOR_ITEM]: [evOnShowRewardsTransparencyPdp],
    [EVENT_CART_MODAL_VIEW]: [evOnShowRewardsTransparencyCartModal],
    [API_ERROR]: [evOnApiError],
    [EVENT_MODIFY_QUANTITY]: [modifyQuantity],
    [EVENT_REMOVE_FROM_CART]: [removeFromCart],
    [EVENT_NAVIGATE_TO_CHECKOUT]: [navigateToCheckout],
    [CART_PAGEVIEW]: [cartView],
    [EVENT_VIEW_CART_CLICK]: [viewCartClick],
    [EVENT_CART_MODAL_CLOSE]: [cartModalClose],
    [ON_FEDERATED_LOGIN_MODAL_PAGE_VIEW]: [evOnShowFederatedLoginModal],
    [SET_HF_FOOT_SUBSCRIBE_SUBMIT]: [handleSubscribeSubmitted],
    [EVENT_CART_RECO_CLICK]: [cartRecommendationClick]
  }
};
